import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, Search, Send, Users, Mail, MailCheck, Trash2, RefreshCw, 
  Copy, BarChart3, Eye, TrendingUp, AlertTriangle, UserMinus, FolderPlus, Tag, Edit2, HelpCircle,
  Upload, FileSpreadsheet, ChevronLeft, ChevronRight, Filter, Calendar, CheckCircle2, Clock, XCircle, Link2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import SimpleTooltip from '../components/SimpleTooltip';
import ContactSyncSettings from '../components/ContactSyncSettings';

const Newsletters = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('newsletters');
  
  // Subscribers state
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscriberSearch, setSubscriberSearch] = useState('');
  const [showAddSubscriber, setShowAddSubscriber] = useState(false);
  const [newSubscriber, setNewSubscriber] = useState({ email: '', name: '', phone: '', company: '', role: '', preferred_language: 'en', group_ids: [] });
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [showEditSubscriber, setShowEditSubscriber] = useState(false);
  
  // Subscriber pagination state
  const [subscriberPage, setSubscriberPage] = useState(1);
  const [subscriberTotal, setSubscriberTotal] = useState(0);
  const [subscriberTotalPages, setSubscriberTotalPages] = useState(1);
  
  // Groups state
  const [groups, setGroups] = useState([]);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });
  const [editingGroup, setEditingGroup] = useState(null);
  const [viewingGroup, setViewingGroup] = useState(null);
  const [showGroupMembers, setShowGroupMembers] = useState(false);
  const [groupMembersSearch, setGroupMembersSearch] = useState('');
  
  // Bulk selection state
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [showAddToGroupDialog, setShowAddToGroupDialog] = useState(false);
  const [addToGroupId, setAddToGroupId] = useState('');  
  // Newsletter Analytics state
  const [showNewsletterAnalytics, setShowNewsletterAnalytics] = useState(false);
  const [selectedNewsletterAnalytics, setSelectedNewsletterAnalytics] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoadingDetail, setAnalyticsLoadingDetail] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [recipientSearch, setRecipientSearch] = useState('');
  
  // Analytics state
  const [campaigns, setCampaigns] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsSummary, setAnalyticsSummary] = useState({
    total_newsletters: 0,
    total_sent: 0,
    total_opened: 0,
    total_bounced: 0,
    open_rate: 0,
    bounce_rate: 0
  });
  
  // Email stats
  const [emailStats, setEmailStats] = useState({
    total_subscribers: 0,
    active_subscribers: 0,
    emails_sent_30d: 0,
    newsletters_sent: 0
  });
  
  // Test email state
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Send newsletter dialog
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [sendToGroups, setSendToGroups] = useState([]);
  const [sendToEmails, setSendToEmails] = useState('');
  const [sendToSubscribers, setSendToSubscribers] = useState([]); // Individual subscriber IDs
  const [subscriberSearchSend, setSubscriberSearchSend] = useState('');
  const [sending, setSending] = useState(false);
  
  // Scheduling state
  const [sendMode, setSendMode] = useState('now'); // 'now' or 'schedule'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  
  // CSV Import state
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvColumns, setCsvColumns] = useState([]);
  const [csvMapping, setCsvMapping] = useState({
    email_column: 0,
    name_column: null,
    group_column: null,
    role_column: null,
    language_column: null,
    has_header: true,
    default_group_ids: [],
    override_existing: false  // New: update existing subscribers on import
  });
  const [csvImporting, setCsvImporting] = useState(false);
  const csvInputRef = useRef(null);
  
  // Newsletter list view state
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'cards'
  const [newsletterPage, setNewsletterPage] = useState(1);
  const [newsletterPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Recipient details dialog state (for clickable stats)
  const [showRecipientDetails, setShowRecipientDetails] = useState(false);
  const [recipientDetailsType, setRecipientDetailsType] = useState(''); // opened, bounced, unsubscribed
  const [recipientDetailsCampaign, setRecipientDetailsCampaign] = useState(null);
  const [recipientDetailsList, setRecipientDetailsList] = useState([]);
  const [recipientDetailsLoading, setRecipientDetailsLoading] = useState(false);
  const [recipientDetailsPage, setRecipientDetailsPage] = useState(1);
  const [recipientDetailsTotal, setRecipientDetailsTotal] = useState(0);
  const [recipientDetailsBounceType, setRecipientDetailsBounceType] = useState('all'); // all, hard, soft

  useEffect(() => {
    fetchNewsletters();
    fetchEmailStats();
    fetchGroups();
    fetchAnalyticsSummary(); // Also load analytics summary for header stats
    fetchCampaigns(); // Fetch campaigns for newsletter list analytics
  }, []);
  
  useEffect(() => {
    if (activeTab === 'subscribers') {
      fetchSubscribers();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    } else if (activeTab === 'groups') {
      fetchGroups();
    }
  }, [activeTab]);
  
  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/newsletters/campaigns');
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  // Track which campaign rows are mid-resync to disable their buttons
  const [resyncingCampaignId, setResyncingCampaignId] = useState(null);

  // Hide tiny test sends (< 10 recipients) from campaign history by default
  const [hideTestSends, setHideTestSends] = useState(true);

  const resyncCampaignAnalytics = async (campaign) => {
    if (!campaign?.campaign_id) return;
    setResyncingCampaignId(campaign.campaign_id);
    try {
      const response = await api.post(`/newsletters/campaigns/${campaign.campaign_id}/resync`);
      const data = response.data || {};
      const applied = data.applied_updates || {};
      const changedKeys = Object.keys(applied).filter(k => k !== 'last_resync_at');
      if (data.note) {
        // Provider had no data for this campaign (e.g. sent before tracking was enhanced)
        toast.info(data.note, { duration: 8000 });
      } else if (changedKeys.length === 0) {
        toast.info('Analytics already up to date — no new events from the email provider.');
      } else {
        toast.success(`Analytics re-synced (${changedKeys.length} counter${changedKeys.length === 1 ? '' : 's'} updated).`);
      }
      await fetchCampaigns();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Re-sync failed';
      toast.error(detail);
    } finally {
      setResyncingCampaignId(null);
    }
  };

  const fetchNewsletters = async () => {
    try {
      const response = await api.get('/newsletters');
      setNewsletters(response.data);
    } catch (error) {
      toast.error('Failed to load newsletters');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSubscribers = async (page = 1) => {
    setSubscribersLoading(true);
    try {
      const response = await api.get(`/subscribers?page=${page}&per_page=50`);
      setSubscribers(response.data.subscribers || []);
      setSubscriberTotal(response.data.total || 0);
      setSubscriberTotalPages(response.data.total_pages || 1);
      setSubscriberPage(page);
    } catch (error) {
      console.error('Failed to load subscribers:', error);
    } finally {
      setSubscribersLoading(false);
    }
  };
  
  // Fetch all subscriber IDs for "Select All" feature
  const fetchAllSubscriberIds = async () => {
    try {
      const response = await api.get('/subscribers?page=1&per_page=10000');
      return (response.data.subscribers || []).map(s => s.subscriber_id);
    } catch (error) {
      console.error('Failed to fetch all subscriber IDs:', error);
      return [];
    }
  };
  
  const fetchGroups = async () => {
    try {
      const response = await api.get('/subscriber-groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };
  
  const fetchAnalyticsSummary = async () => {
    try {
      const response = await api.get('/newsletters/analytics/summary');
      setAnalyticsSummary(response.data);
    } catch (error) {
      console.error('Failed to load analytics summary:', error);
    }
  };
  
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [summaryRes, campaignsRes] = await Promise.all([
        api.get('/newsletters/analytics/summary'),
        api.get('/newsletters/campaigns')
      ]);
      setAnalyticsSummary(summaryRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };
  
  const fetchEmailStats = async () => {
    try {
      const response = await api.get('/newsletters/analytics/summary');
      setEmailStats({
        total_subscribers: response.data.total_subscribers || 0,
        active_subscribers: response.data.total_subscribers || 0,
        emails_sent_30d: response.data.total_sent || 0,
        newsletters_sent: response.data.total_newsletters || 0
      });
    } catch (error) {
      console.error('Failed to load email stats');
    }
  };
  
  const handleAddSubscriber = async () => {
    if (!newSubscriber.email) {
      toast.error('Email is required');
      return;
    }
    
    // Check if multiple emails (comma or newline separated)
    const emails = newSubscriber.email
      .split(/[,\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));
    
    if (emails.length === 0) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      if (emails.length === 1) {
        // Single subscriber
        await api.post('/subscribers', {
          email: emails[0],
          name: newSubscriber.name || null,
          phone: newSubscriber.phone || null,
          company: newSubscriber.company || null,
          role: newSubscriber.role || null,
          preferred_language: newSubscriber.preferred_language || 'en',
          group_ids: newSubscriber.group_ids,
          source: 'manual'
        });
        toast.success('Subscriber added successfully');
      } else {
        // Multiple subscribers - use bulk endpoint
        const response = await api.post('/subscribers/bulk', {
          emails: emails,
          group_ids: newSubscriber.group_ids,
          preferred_language: newSubscriber.preferred_language || 'en'
        });
        const result = response.data;
        toast.success(`Added ${result.created} subscriber(s)${result.duplicates > 0 ? `, ${result.duplicates} duplicate(s) skipped` : ''}`);
      }
      
      setShowAddSubscriber(false);
      setNewSubscriber({ email: '', name: '', phone: '', company: '', role: '', preferred_language: 'en', group_ids: [] });
      fetchSubscribers();
      fetchEmailStats();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      if (Array.isArray(detail)) {
        const messages = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
        toast.error(messages);
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || 'Failed to add subscriber');
      } else {
        toast.error(detail || 'Failed to add subscriber');
      }
    }
  };
  
  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    
    try {
      await api.delete(`/subscribers/${subscriberId}`);
      toast.success('Subscriber deleted');
      fetchSubscribers();
      fetchEmailStats();
    } catch (error) {
      toast.error('Failed to delete subscriber');
    }
  };
  
  const openEditSubscriber = (subscriber) => {
    setEditingSubscriber({
      ...subscriber,
      group_ids: subscriber.group_ids || []
    });
    setShowEditSubscriber(true);
  };
  
  const handleUpdateSubscriber = async () => {
    if (!editingSubscriber) return;
    
    try {
      await api.put(`/subscribers/${editingSubscriber.subscriber_id}`, {
        name: editingSubscriber.name || null,
        role: editingSubscriber.role || null,
        group_ids: editingSubscriber.group_ids,
        status: editingSubscriber.status,
        preferred_language: editingSubscriber.preferred_language || 'en'
      });
      toast.success('Subscriber updated successfully');
      setShowEditSubscriber(false);
      setEditingSubscriber(null);
      fetchSubscribers();
      fetchGroups(); // Refresh group counts
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(err => err.msg || 'Error').join(', '));
      } else {
        toast.error(detail || 'Failed to update subscriber');
      }
    }
  };
  
  // Helper to get group names from IDs
  const getGroupNames = (groupIds) => {
    if (!groupIds || groupIds.length === 0) return [];
    return groupIds.map(id => {
      const group = groups.find(g => g.group_id === id);
      return group ? group.name : null;
    }).filter(Boolean);
  };
  
  const handleAddGroup = async () => {
    if (!newGroup.name) {
      toast.error('Group name is required');
      return;
    }
    
    try {
      if (editingGroup) {
        await api.put(`/subscriber-groups/${editingGroup.group_id}`, newGroup);
        toast.success('Group updated successfully');
      } else {
        await api.post('/subscriber-groups', newGroup);
        toast.success('Group created successfully');
      }
      setShowAddGroup(false);
      setNewGroup({ name: '', description: '' });
      setEditingGroup(null);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save group');
    }
  };
  
  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    
    try {
      await api.delete(`/subscriber-groups/${groupId}`);
      toast.success('Group deleted');
      fetchGroups();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };
  
  const handleCloneNewsletter = async (e, newsletterId) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const response = await api.post(`/newsletters/${newsletterId}/clone`);
      toast.success('Newsletter cloned successfully');
      fetchNewsletters();
    } catch (error) {
      toast.error('Failed to clone newsletter');
    }
  };
  
  const handleDeleteNewsletter = async (e, newsletterId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this newsletter?')) return;
    
    try {
      await api.delete(`/newsletters/${newsletterId}`);
      toast.success('Newsletter deleted');
      fetchNewsletters();
    } catch (error) {
      toast.error('Failed to delete newsletter');
    }
  };
  
  const openGroupMembers = (group) => {
    setViewingGroup(group);
    setGroupMembersSearch('');
    setShowGroupMembers(true);
    // Fetch subscribers if not loaded
    if (subscribers.length === 0) {
      fetchSubscribers();
    }
  };
  
  const handleRemoveFromGroup = async (subscriberId, groupId) => {
    try {
      // Get current subscriber
      const subscriber = subscribers.find(s => s.subscriber_id === subscriberId);
      if (!subscriber) return;
      
      // Remove group from subscriber's group_ids
      const newGroupIds = (subscriber.group_ids || []).filter(id => id !== groupId);
      
      await api.put(`/subscribers/${subscriberId}`, {
        group_ids: newGroupIds
      });
      
      toast.success('Subscriber removed from group');
      fetchSubscribers();
      fetchGroups();
    } catch (error) {
      toast.error('Failed to remove subscriber from group');
    }
  };
  
  const getGroupSubscribers = (groupId) => {
    return subscribers.filter(s => 
      s.group_ids && s.group_ids.includes(groupId) && s.status === 'active'
    );
  };
  
  const openNewsletterAnalytics = async (e, newsletter) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedNewsletterAnalytics(newsletter);
    setShowNewsletterAnalytics(true);
    setAnalyticsLoadingDetail(true);
    setRecipientFilter('all');
    setRecipientSearch('');
    
    try {
      const response = await api.get(`/newsletters/${newsletter.newsletter_id}/analytics`);
      setAnalyticsData(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
      console.error('Analytics error:', error);
    } finally {
      setAnalyticsLoadingDetail(false);
    }
  };
  
  const getFilteredRecipients = () => {
    if (!analyticsData?.recipients) return [];
    
    let filtered = analyticsData.recipients;
    
    // Apply status filter
    if (recipientFilter !== 'all') {
      filtered = filtered.filter(r => {
        if (recipientFilter === 'opened') return r.opened_at;
        if (recipientFilter === 'clicked') return r.clicked_at;
        if (recipientFilter === 'bounced') return r.status?.includes('bounce') || r.bounced_at;
        if (recipientFilter === 'delivered') return r.status === 'delivered' || r.status === 'sent';
        return true;
      });
    }
    
    // Apply search
    if (recipientSearch) {
      filtered = filtered.filter(r => 
        r.email.toLowerCase().includes(recipientSearch.toLowerCase()) ||
        (r.name || '').toLowerCase().includes(recipientSearch.toLowerCase())
      );
    }
    
    return filtered;
  };

  // Fetch recipient details for clickable stats
  const fetchRecipientDetails = async (campaign, statusType, bounceType = 'all', page = 1) => {
    setRecipientDetailsLoading(true);
    setRecipientDetailsCampaign(campaign);
    setRecipientDetailsType(statusType);
    setRecipientDetailsPage(page);
    setRecipientDetailsBounceType(bounceType);
    setShowRecipientDetails(true);
    
    try {
      let url = `/campaigns/${campaign.campaign_id}/recipients?status=${statusType}&page=${page}&per_page=50`;
      if (statusType === 'bounced' && bounceType !== 'all') {
        url += `&bounce_type=${bounceType}`;
      }
      const response = await api.get(url);
      setRecipientDetailsList(response.data.recipients || []);
      setRecipientDetailsTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load recipient details');
      console.error('Recipient details error:', error);
      setRecipientDetailsList([]);
    } finally {
      setRecipientDetailsLoading(false);
    }
  };

  // Export recipient details as CSV
  const exportRecipientDetails = async () => {
    if (!recipientDetailsCampaign) return;
    
    try {
      let url = `/campaigns/${recipientDetailsCampaign.campaign_id}/recipients/export?status=${recipientDetailsType}`;
      if (recipientDetailsType === 'bounced' && recipientDetailsBounceType !== 'all') {
        url += `&bounce_type=${recipientDetailsBounceType}`;
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${recipientDetailsCampaign.title || 'campaign'}_${recipientDetailsType}_recipients.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
      console.error('Export error:', error);
    }
  };
  
  const openSendDialog = (e, newsletter) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedNewsletter(newsletter);
    setSendToGroups([]);
    setSendToEmails('');
    setSendToSubscribers([]);
    setSubscriberSearchSend('');
    setSendMode('now');
    setScheduleDate('');
    setScheduleTime('');
    setShowSendDialog(true);
    // Fetch latest subscribers if not loaded
    if (subscribers.length === 0) {
      fetchSubscribers();
    }
  };
  
  const handleSendNewsletter = async () => {
    // Get emails from selected individual subscribers
    const subscriberEmails = sendToSubscribers.map(subId => {
      const sub = subscribers.find(s => s.subscriber_id === subId);
      return sub?.email;
    }).filter(Boolean);
    
    // Get custom emails
    const customEmails = sendToEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e && e.includes('@'));
    
    // Combine all emails
    const allEmails = [...new Set([...subscriberEmails, ...customEmails])];
    
    if (sendToGroups.length === 0 && allEmails.length === 0) {
      toast.error('Please select groups, subscribers, or enter email addresses');
      return;
    }
    
    // Validate schedule if scheduling
    let scheduledAt = null;
    if (sendMode === 'schedule') {
      if (!scheduleDate || !scheduleTime) {
        toast.error('Please select both date and time for scheduling');
        return;
      }
      
      const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
      if (scheduledDateTime <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return;
      }
      scheduledAt = scheduledDateTime.toISOString();
    }
    
    setSending(true);
    try {
      const payload = {
        recipient_emails: allEmails,
        group_ids: sendToGroups
      };
      
      if (scheduledAt) {
        payload.scheduled_at = scheduledAt;
      }
      
      const response = await api.post(`/newsletters/${selectedNewsletter.newsletter_id}/send`, payload);
      
      // Calculate total recipients
      const groupSubscriberCount = sendToGroups.reduce((acc, gid) => {
        const group = groups.find(g => g.group_id === gid);
        return acc + (group?.subscriber_count || 0);
      }, 0);
      const totalRecipients = groupSubscriberCount + allEmails.length;
      
      if (scheduledAt) {
        toast.success(`Newsletter scheduled for ${new Date(scheduledAt).toLocaleString()} (${totalRecipients} recipients)`);
      } else {
        toast.success(`Newsletter is being sent to ${totalRecipients} recipients!`);
      }
      
      setShowSendDialog(false);
      fetchNewsletters();
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send newsletter');
    } finally {
      setSending(false);
    }
  };
  
  const handleCancelSchedule = async (e, newsletter) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!confirm('Are you sure you want to cancel this scheduled newsletter?')) {
      return;
    }
    
    try {
      await api.delete(`/newsletters/${newsletter.newsletter_id}/schedule`);
      toast.success('Scheduled newsletter cancelled');
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel scheduled newsletter');
    }
  };
  
  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingTest(true);
    try {
      const response = await api.post('/email/test', { to_email: testEmail });
      if (response.data.success) {
        toast.success('Test email sent successfully!');
        setShowTestEmail(false);
        setTestEmail('');
      } else {
        toast.error('Failed to send test email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };
  
  // CSV Import handlers
  const handleCsvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setCsvFile(file);
    
    // Read and parse CSV for preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === 'string') {
        const lines = text.split('\n').filter(line => line.trim());
        const rows = lines.map(line => {
          // Handle quoted values
          const result = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        });
        
        if (rows.length > 0) {
          setCsvColumns(rows[0].map((_, i) => i));
          setCsvPreview(rows.slice(0, 6)); // First 6 rows for preview
          
          // Auto-detect columns
          const headerRow = rows[0].map(h => h.toLowerCase());
          const emailIdx = headerRow.findIndex(h => h.includes('email'));
          const nameIdx = headerRow.findIndex(h => h.includes('name') && !h.includes('email'));
          const groupIdx = headerRow.findIndex(h => h.includes('group') || h.includes('category') || h.includes('list'));
          const roleIdx = headerRow.findIndex(h => h.includes('role') || h.includes('type') || h.includes('segment'));
          
          setCsvMapping(prev => ({
            ...prev,
            email_column: emailIdx >= 0 ? emailIdx : 0,
            name_column: nameIdx >= 0 ? nameIdx : null,
            group_column: groupIdx >= 0 ? groupIdx : null,
            role_column: roleIdx >= 0 ? roleIdx : null
          }));
        }
      }
    };
    reader.readAsText(file);
  };
  
  const handleCsvImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setCsvImporting(true);
    try {
      const text = await csvFile.text();
      
      const response = await api.post('/subscribers/import-csv', {
        csv_data: text,
        mapping: csvMapping
      });
      
      const result = response.data;
      
      if (result.success) {
        let message = `Imported ${result.created} subscribers`;
        if (result.updated > 0) {
          message += `, updated ${result.updated} existing`;
        }
        if (result.duplicates > 0) {
          message += `, ${result.duplicates} duplicates skipped`;
        }
        if (result.errors > 0) {
          message += `, ${result.errors} errors`;
        }
        if (result.groups_created?.length > 0) {
          message += `. Created ${result.groups_created.length} new group(s)`;
        }
        
        toast.success(message);
        setShowCsvImport(false);
        setCsvFile(null);
        setCsvPreview([]);
        fetchSubscribers();
        fetchGroups();
        fetchEmailStats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setCsvImporting(false);
    }
  };
  
  const resetCsvImport = () => {
    setCsvFile(null);
    setCsvPreview([]);
    setCsvMapping({
      email_column: 0,
      name_column: null,
      group_column: null,
      role_column: null,
      language_column: null,
      has_header: true,
      default_group_ids: [],
      override_existing: false
    });
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
    }
  };

  // Newsletter filtering and pagination
  const getFilteredNewsletters = () => {
    let filtered = newsletters.filter((newsletter) =>
      newsletter.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(nl => {
        if (statusFilter === 'sent') return nl.sent_at && nl.status !== 'scheduled';
        if (statusFilter === 'scheduled') return nl.status === 'scheduled' || nl.scheduled_at;
        if (statusFilter === 'draft') return !nl.sent_at && nl.status !== 'scheduled' && !nl.scheduled_at;
        return true;
      });
    }
    
    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(nl => {
        const date = new Date(nl.created_at);
        if (dateFilter === '7days') {
          return (now - date) / (1000 * 60 * 60 * 24) <= 7;
        }
        if (dateFilter === '30days') {
          return (now - date) / (1000 * 60 * 60 * 24) <= 30;
        }
        if (dateFilter === '90days') {
          return (now - date) / (1000 * 60 * 60 * 24) <= 90;
        }
        return true;
      });
    }
    
    return filtered;
  };
  
  const filteredNewsletters = getFilteredNewsletters();
  const totalNewsletterPages = Math.ceil(filteredNewsletters.length / newsletterPerPage);
  const paginatedNewsletters = filteredNewsletters.slice(
    (newsletterPage - 1) * newsletterPerPage,
    newsletterPage * newsletterPerPage
  );
  
  // Get campaign analytics for a newsletter
  const getNewsletterAnalytics = (newsletterId) => {
    const campaign = campaigns.find(c => c.newsletter_id === newsletterId);
    return campaign || null;
  };
  
  const filteredSubscribers = subscribers.filter((sub) =>
    sub.email.toLowerCase().includes(subscriberSearch.toLowerCase()) ||
    (sub.name || '').toLowerCase().includes(subscriberSearch.toLowerCase())
  );

  // Bulk selection functions
  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all on current page
      setSelectedSubscribers(filteredSubscribers.map(s => s.subscriber_id));
    } else {
      setSelectedSubscribers([]);
    }
  };

  // Select ALL subscribers across all pages
  const handleSelectAllPages = async () => {
    setSubscribersLoading(true);
    try {
      const allIds = await fetchAllSubscriberIds();
      setSelectedSubscribers(allIds);
      toast.success(`Selected all ${allIds.length} subscribers`);
    } catch (error) {
      toast.error('Failed to select all subscribers');
    } finally {
      setSubscribersLoading(false);
    }
  };

  const handleSelectSubscriber = (subscriberId, checked) => {
    if (checked) {
      setSelectedSubscribers(prev => [...prev, subscriberId]);
    } else {
      setSelectedSubscribers(prev => prev.filter(id => id !== subscriberId));
    }
  };

  const isAllSelected = filteredSubscribers.length > 0 && 
    filteredSubscribers.every(s => selectedSubscribers.includes(s.subscriber_id));

  const isSomeSelected = selectedSubscribers.length > 0 && !isAllSelected;
  
  const isAllPagesSelected = selectedSubscribers.length === subscriberTotal && subscriberTotal > 0;

  // Export subscribers to CSV
  const handleExportCSV = async () => {
    let subscribersToExport;
    
    if (selectedSubscribers.length > 0) {
      // If we have selections that span multiple pages, fetch all selected
      if (selectedSubscribers.length > subscribers.length) {
        try {
          // Fetch all subscribers for export
          const response = await api.get('/subscribers?page=1&per_page=10000');
          const allSubs = response.data.subscribers || [];
          subscribersToExport = allSubs.filter(s => selectedSubscribers.includes(s.subscriber_id));
        } catch (error) {
          toast.error('Failed to fetch subscribers for export');
          return;
        }
      } else {
        subscribersToExport = subscribers.filter(s => selectedSubscribers.includes(s.subscriber_id));
      }
    } else {
      // Export all - fetch all subscribers
      try {
        const response = await api.get('/subscribers?page=1&per_page=10000');
        subscribersToExport = response.data.subscribers || [];
      } catch (error) {
        toast.error('Failed to fetch subscribers for export');
        return;
      }
    }
    
    if (subscribersToExport.length === 0) {
      toast.error('No subscribers to export');
      return;
    }

    // Create CSV content
    const headers = ['email', 'name', 'status', 'language', 'groups', 'subscribed_date'];
    const rows = subscribersToExport.map(sub => {
      const groupNames = getGroupNames(sub.group_ids).join('; ');
      return [
        sub.email,
        sub.name || '',
        sub.status || 'active',
        sub.preferred_language || sub.language || '',
        groupNames,
        sub.created_at ? new Date(sub.created_at).toISOString().split('T')[0] : ''
      ].map(val => `"${(val || '').replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    
    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${subscribersToExport.length} subscriber(s) to CSV`);
  };

  // Add selected subscribers to a group - using bulk endpoint
  const handleAddToGroup = async () => {
    if (!addToGroupId || selectedSubscribers.length === 0) {
      toast.error('Please select a group and at least one subscriber');
      return;
    }

    // Show loading state
    toast.info(`Adding ${selectedSubscribers.length} subscribers to group... Please wait.`);

    try {
      // Use the bulk endpoint for efficiency with extended timeout
      const response = await api.post('/subscribers/bulk-add-to-group', {
        subscriber_ids: selectedSubscribers,
        group_id: addToGroupId
      }, {
        timeout: 120000  // 2 minute timeout for large operations
      });
      
      toast.success(response.data.message || `Added ${response.data.updated_count} subscriber(s) to group`);
      setShowAddToGroupDialog(false);
      setAddToGroupId('');
      setSelectedSubscribers([]);
      fetchSubscribers(subscriberPage);
      fetchGroups(); // Refresh group counts
    } catch (error) {
      // Check if it might have succeeded despite the error (timeout case)
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.warning('Request timed out, but operation may have completed. Please check the group count.');
        setShowAddToGroupDialog(false);
        fetchGroups();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add subscribers to group');
      }
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedSubscribers([]);
  };

  return (
    <div className="flex" data-testid="newsletters-page">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Email & Newsletters
              </h1>
              <p className="text-slate-500 text-sm mt-1">Manage newsletters, subscribers, and analytics</p>
            </div>
            <div className="flex gap-3">
              <SimpleTooltip content="Send a test email to verify your setup">
                <Button variant="outline" onClick={() => setShowTestEmail(true)}>
                  <MailCheck size={18} className="mr-2" />
                  Test Email
                </Button>
              </SimpleTooltip>
              <SimpleTooltip content="Create a new email newsletter campaign">
                <Link to="/newsletters/new">
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-newsletter-button">
                    <Plus size={18} className="mr-2" />
                    Create Newsletter
                  </Button>
                </Link>
              </SimpleTooltip>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <SimpleTooltip content="Active email subscribers in your list">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Users className="text-indigo-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{emailStats.active_subscribers}</p>
                    <p className="text-xs text-slate-500">Subscribers</p>
                  </div>
                </div>
              </div>
            </SimpleTooltip>
            <SimpleTooltip content="Total emails sent in the last 30 days">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Mail className="text-emerald-600" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{emailStats.emails_sent_30d}</p>
                    <p className="text-xs text-slate-500">Emails Sent</p>
                  </div>
                </div>
              </div>
            </SimpleTooltip>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Eye className="text-amber-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analyticsSummary.open_rate}%</p>
                  <p className="text-xs text-slate-500">Open Rate</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Send className="text-purple-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{emailStats.newsletters_sent}</p>
                  <p className="text-xs text-slate-500">Campaigns</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="newsletters" className="gap-2">
                <Mail size={16} />
                Newsletters
              </TabsTrigger>
              <TabsTrigger value="subscribers" className="gap-2">
                <Users size={16} />
                Subscribers
              </TabsTrigger>
              <TabsTrigger value="groups" className="gap-2">
                <Tag size={16} />
                Groups
              </TabsTrigger>
              <TabsTrigger value="analytics" className="gap-2">
                <BarChart3 size={16} />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="contact-sync" className="gap-2" data-testid="contact-sync-tab">
                <Link2 size={16} />
                Contact Sync
              </TabsTrigger>
            </TabsList>
            
            {/* Newsletters Tab */}
            <TabsContent value="newsletters">
              {/* Search and Filters */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
                <div className="flex gap-4 items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                    <Input
                      placeholder="Search newsletters..."
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setNewsletterPage(1); }}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                  
                  {/* Status Filter */}
                  <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setNewsletterPage(1); }}>
                    <SelectTrigger className="w-[140px]">
                      <Filter size={16} className="mr-2 text-slate-400" />
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Date Filter */}
                  <Select value={dateFilter} onValueChange={(val) => { setDateFilter(val); setNewsletterPage(1); }}>
                    <SelectTrigger className="w-[140px]">
                      <Calendar size={16} className="mr-2 text-slate-400" />
                      <SelectValue placeholder="Date" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="7days">Last 7 Days</SelectItem>
                      <SelectItem value="30days">Last 30 Days</SelectItem>
                      <SelectItem value="90days">Last 90 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">Loading newsletters...</p>
                </div>
              ) : filteredNewsletters.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Send size={48} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500 mb-4">No newsletters found</p>
                  <Link to="/newsletters/new">
                    <Button data-testid="empty-create-button">
                      <Plus size={18} className="mr-2" />
                      Create Your First Newsletter
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  {/* List View Table */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Title</th>
                          <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Status</th>
                          <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Recipients</th>
                          <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Open Rate</th>
                          <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Click Rate</th>
                          <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Date</th>
                          <th className="text-right px-6 py-3 text-sm font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {paginatedNewsletters.map((newsletter) => {
                          const analytics = getNewsletterAnalytics(newsletter.newsletter_id);
                          const openRate = analytics?.open_rate || 0;
                          const clickRate = analytics?.click_rate || 0;
                          
                          return (
                            <tr key={newsletter.newsletter_id} className="hover:bg-slate-50 group">
                              <td className="px-6 py-4">
                                <Link 
                                  to={`/newsletters/${newsletter.newsletter_id}`}
                                  className="block"
                                  data-testid={`newsletter-row-${newsletter.newsletter_id}`}
                                >
                                  <p className="font-medium text-slate-900 group-hover:text-indigo-600 transition-colors">{newsletter.title}</p>
                                  <p className="text-sm text-slate-500 truncate max-w-xs">{newsletter.subject}</p>
                                </Link>
                              </td>
                              <td className="px-6 py-4">
                                {newsletter.sent_at && newsletter.status !== 'scheduled' ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">
                                    <CheckCircle2 size={12} />
                                    Sent
                                  </span>
                                ) : (newsletter.status === 'scheduled' || newsletter.scheduled_at) ? (
                                  <div>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-indigo-100 text-indigo-700">
                                      <Calendar size={12} />
                                      Scheduled
                                    </span>
                                    {newsletter.scheduled_at && (
                                      <p className="text-xs text-indigo-600 mt-1">
                                        {new Date(newsletter.scheduled_at).toLocaleString()}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-slate-100 text-slate-600">
                                    <Clock size={12} />
                                    Draft
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-slate-600">
                                  {newsletter.sent_at || newsletter.scheduled_at ? (newsletter.recipients_count || 0) : '-'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                {newsletter.sent_at && newsletter.status !== 'scheduled' ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                      <div 
                                        className={`h-full rounded-full ${openRate > 30 ? 'bg-emerald-500' : openRate > 15 ? 'bg-amber-500' : 'bg-red-400'}`}
                                        style={{ width: `${Math.min(openRate, 100)}%` }}
                                      />
                                    </div>
                                    <span className="text-sm text-slate-600">{openRate}%</span>
                                  </div>
                                ) : (
                                  <span className="text-sm text-slate-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                {newsletter.sent_at && newsletter.status !== 'scheduled' ? (
                                  <span className="text-sm text-slate-600">{clickRate}%</span>
                                ) : (
                                  <span className="text-sm text-slate-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-slate-500">
                                  {newsletter.sent_at 
                                    ? new Date(newsletter.sent_at).toLocaleDateString()
                                    : newsletter.scheduled_at
                                    ? new Date(newsletter.scheduled_at).toLocaleDateString()
                                    : new Date(newsletter.created_at).toLocaleDateString()
                                  }
                                </span>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                                  {newsletter.sent_at && newsletter.status !== 'scheduled' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                      onClick={(e) => openNewsletterAnalytics(e, newsletter)}
                                      title="Analytics"
                                      data-testid={`analytics-btn-${newsletter.newsletter_id}`}
                                    >
                                      <BarChart3 size={14} />
                                    </Button>
                                  )}
                                  {(newsletter.status === 'scheduled' || newsletter.scheduled_at) && !newsletter.sent_at ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                      onClick={(e) => handleCancelSchedule(e, newsletter)}
                                      title="Cancel Schedule"
                                    >
                                      <XCircle size={14} />
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0"
                                      onClick={(e) => openSendDialog(e, newsletter)}
                                      title="Send"
                                    >
                                      <Send size={14} />
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                    onClick={(e) => handleCloneNewsletter(e, newsletter.newsletter_id)}
                                    title="Clone"
                                  >
                                    <Copy size={14} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={(e) => handleDeleteNewsletter(e, newsletter.newsletter_id)}
                                    title="Delete"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {totalNewsletterPages > 1 && (
                    <div className="flex items-center justify-between mt-4 px-2">
                      <p className="text-sm text-slate-500">
                        Showing {((newsletterPage - 1) * newsletterPerPage) + 1} to {Math.min(newsletterPage * newsletterPerPage, filteredNewsletters.length)} of {filteredNewsletters.length} newsletters
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setNewsletterPage(p => Math.max(1, p - 1))}
                          disabled={newsletterPage === 1}
                        >
                          <ChevronLeft size={16} className="mr-1" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setNewsletterPage(p => Math.min(totalNewsletterPages, p + 1))}
                          disabled={newsletterPage === totalNewsletterPages}
                        >
                          Next
                          <ChevronRight size={16} className="ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
            
            {/* Subscribers Tab */}
            <TabsContent value="subscribers">
              <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                  <Input
                    placeholder="Search subscribers..."
                    value={subscriberSearch}
                    onChange={(e) => setSubscriberSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button onClick={() => fetchSubscribers()} variant="outline">
                  <RefreshCw size={16} className="mr-2" />
                  Refresh
                </Button>
                <Button onClick={handleExportCSV} variant="outline" data-testid="export-csv-button">
                  <FileSpreadsheet size={16} className="mr-2" />
                  {selectedSubscribers.length > 0 ? `Export Selected (${selectedSubscribers.length})` : 'Export All'}
                </Button>
                <Button onClick={() => { resetCsvImport(); setShowCsvImport(true); }} variant="outline" data-testid="import-csv-button">
                  <Upload size={16} className="mr-2" />
                  Import CSV
                </Button>
                <Button onClick={() => setShowAddSubscriber(true)} data-testid="add-subscriber-button">
                  <Plus size={16} className="mr-2" />
                  Add Subscriber
                </Button>
              </div>
              
              {/* Bulk Actions Bar - shown when subscribers selected */}
              {selectedSubscribers.length > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-sm font-medium text-indigo-700">
                      {selectedSubscribers.length} of {subscriberTotal} subscriber(s) selected
                    </span>
                    {/* Show "Select All X" option if there are more subscribers than selected */}
                    {!isAllPagesSelected && subscriberTotal > selectedSubscribers.length && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleSelectAllPages}
                        className="text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Select all {subscriberTotal} subscribers
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearSelection}
                      className="text-indigo-600 hover:text-indigo-700"
                    >
                      Clear selection
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddToGroupDialog(true)}
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                    >
                      <FolderPlus size={16} className="mr-2" />
                      Add to Group
                    </Button>
                  </div>
                </div>
              )}
              
              {subscribersLoading ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">Loading subscribers...</p>
                </div>
              ) : filteredSubscribers.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Users size={48} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500 mb-4">No subscribers yet</p>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => { resetCsvImport(); setShowCsvImport(true); }} variant="outline">
                      <Upload size={18} className="mr-2" />
                      Import from CSV
                    </Button>
                    <Button onClick={() => setShowAddSubscriber(true)}>
                      <Plus size={18} className="mr-2" />
                      Add Subscriber
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 w-12">
                          <Checkbox
                            checked={isAllSelected}
                            onCheckedChange={handleSelectAll}
                            className={isSomeSelected ? 'data-[state=checked]:bg-indigo-400' : ''}
                            data-testid="select-all-subscribers"
                          />
                        </th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Email</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Name</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Source</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Groups</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Subscribed</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredSubscribers.map((subscriber) => {
                        const groupNames = getGroupNames(subscriber.group_ids);
                        const isSelected = selectedSubscribers.includes(subscriber.subscriber_id);
                        return (
                        <tr 
                          key={subscriber.subscriber_id} 
                          className={`hover:bg-slate-50 group ${isSelected ? 'bg-indigo-50' : ''}`}
                        >
                          <td className="px-4 py-4 w-12">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => handleSelectSubscriber(subscriber.subscriber_id, checked)}
                              data-testid={`select-subscriber-${subscriber.subscriber_id}`}
                            />
                          </td>
                          <td className="px-4 py-4 text-sm font-medium">{subscriber.email}</td>
                          <td className="px-4 py-4 text-sm text-slate-600">{subscriber.name || '-'}</td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              subscriber.status === 'active' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {subscriber.status}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              subscriber.source === 'taptile' ? 'bg-purple-100 text-purple-700' :
                              subscriber.source === 'website-form' ? 'bg-blue-100 text-blue-700' :
                              subscriber.source === 'csv-import' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {subscriber.source || 'manual'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            {groupNames.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {groupNames.slice(0, 2).map((name, idx) => (
                                  <span key={idx} className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">
                                    {name}
                                  </span>
                                ))}
                                {groupNames.length > 2 && (
                                  <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                                    +{groupNames.length - 2} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-slate-400">No groups</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {subscriber.created_at ? new Date(subscriber.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openEditSubscriber(subscriber)}
                                className="h-8 w-8 p-0"
                                title="Edit"
                              >
                                <Edit2 size={14} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteSubscriber(subscriber.subscriber_id)}
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Pagination Controls */}
              {subscriberTotal > 50 && (
                <div className="mt-4 flex items-center justify-between bg-white rounded-xl border border-slate-200 p-4">
                  <div className="text-sm text-slate-600">
                    Showing {((subscriberPage - 1) * 50) + 1} - {Math.min(subscriberPage * 50, subscriberTotal)} of {subscriberTotal} subscribers
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchSubscribers(subscriberPage - 1)}
                      disabled={subscriberPage <= 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-slate-600 px-3">
                      Page {subscriberPage} of {subscriberTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchSubscribers(subscriberPage + 1)}
                      disabled={subscriberPage >= subscriberTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
            
            {/* Groups Tab */}
            <TabsContent value="groups">
              <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex justify-between items-center">
                <p className="text-slate-600">Organize your subscribers into groups for targeted campaigns</p>
                <Button onClick={() => { setEditingGroup(null); setNewGroup({ name: '', description: '' }); setShowAddGroup(true); }}>
                  <FolderPlus size={16} className="mr-2" />
                  Create Group
                </Button>
              </div>
              
              {groups.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Tag size={48} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500 mb-4">No groups yet</p>
                  <Button onClick={() => setShowAddGroup(true)}>
                    <FolderPlus size={18} className="mr-2" />
                    Create Your First Group
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groups.map((group) => (
                    <div 
                      key={group.group_id} 
                      className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => openGroupMembers(group)}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold">{group.name}</h3>
                          {group.description && (
                            <p className="text-sm text-slate-500 mt-1">{group.description}</p>
                          )}
                        </div>
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingGroup(group); setNewGroup({ name: group.name, description: group.description || '' }); setShowAddGroup(true); }}
                          >
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteGroup(group.group_id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Users size={16} />
                          <span className="text-sm">{group.subscriber_count || 0} subscribers</span>
                        </div>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openGroupMembers(group); }}>
                          <Eye size={14} className="mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Analytics Tab */}
            <TabsContent value="analytics">
              {analyticsLoading ? (
                <div className="text-center py-12">
                  <p className="text-slate-500">Loading analytics...</p>
                </div>
              ) : (
                <>
                  {/* Analytics Summary Cards */}
                  <div className="grid grid-cols-5 gap-4 mb-6">
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Send size={18} className="text-indigo-600" />
                        <span className="text-sm text-slate-600">Total Sent</span>
                      </div>
                      <p className="text-2xl font-bold">{analyticsSummary.total_sent}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye size={18} className="text-emerald-600" />
                        <span className="text-sm text-slate-600">Opened</span>
                      </div>
                      <p className="text-2xl font-bold">{analyticsSummary.total_opened}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp size={18} className="text-amber-600" />
                        <span className="text-sm text-slate-600">Open Rate</span>
                      </div>
                      <p className="text-2xl font-bold">{analyticsSummary.open_rate}%</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={18} className="text-red-500" />
                        <span className="text-sm text-slate-600">Bounced</span>
                      </div>
                      <p className="text-2xl font-bold">{analyticsSummary.total_bounced}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <UserMinus size={18} className="text-slate-500" />
                        <span className="text-sm text-slate-600">Unsubscribed</span>
                      </div>
                      <p className="text-2xl font-bold">{analyticsSummary.total_unsubscribed || 0}</p>
                    </div>
                  </div>
                  
                  {/* Campaigns List */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                      <h3 className="font-semibold">Campaign History</h3>
                      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="hide-test-sends-toggle">
                        <input
                          type="checkbox"
                          checked={hideTestSends}
                          onChange={(e) => setHideTestSends(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>Hide test sends (&lt; 10 recipients)</span>
                      </label>
                    </div>
                    {campaigns.length === 0 ? (
                      <div className="p-12 text-center">
                        <BarChart3 size={48} className="mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">No campaigns sent yet</p>
                      </div>
                    ) : (
                      <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Newsletter</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Sent</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Opened</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Open Rate</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Bounced</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Unsubscribed</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Status</th>
                            <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Date</th>
                            <th className="text-right px-6 py-3 text-sm font-medium text-slate-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {campaigns
                            .filter(c => !hideTestSends || (c.sent_count || 0) >= 10)
                            .map((campaign) => (
                            <tr key={campaign.campaign_id} className="hover:bg-slate-50">
                              <td className="px-6 py-4 text-sm font-medium">{campaign.title}</td>
                              <td className="px-6 py-4 text-sm">{campaign.sent_count}</td>
                              <td className="px-6 py-4 text-sm">
                                <button
                                  onClick={() => fetchRecipientDetails(campaign, 'opened')}
                                  className="text-emerald-600 hover:text-emerald-800 hover:underline font-medium"
                                  title="Click to see who opened"
                                >
                                  {campaign.opened_count || 0}
                                </button>
                              </td>
                              <td className="px-6 py-4 text-sm">
                                <span className={`font-medium ${campaign.open_rate > 20 ? 'text-emerald-600' : 'text-slate-600'}`}>
                                  {campaign.open_rate}%
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm">
                                <button
                                  onClick={() => fetchRecipientDetails(campaign, 'bounced')}
                                  className="text-red-600 hover:text-red-800 hover:underline font-medium"
                                  title="Click to see bounced emails"
                                >
                                  {campaign.bounced_count || ((campaign.hard_bounced_count || 0) + (campaign.soft_bounced_count || 0))}
                                </button>
                              </td>
                              <td className="px-6 py-4 text-sm">
                                <button
                                  onClick={() => fetchRecipientDetails(campaign, 'unsubscribed')}
                                  className="text-amber-600 hover:text-amber-800 hover:underline font-medium"
                                  title="Click to see unsubscribes"
                                >
                                  {campaign.unsubscribed_count || 0}
                                </button>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  campaign.status === 'completed' 
                                    ? 'bg-emerald-100 text-emerald-700' 
                                    : campaign.status === 'sending'
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-slate-100 text-slate-600'
                                }`}>
                                  {campaign.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-600">
                                {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="px-6 py-4 text-sm text-right">
                                <SimpleTooltip content="Pull the latest delivered / opened / clicked numbers from the email provider. Useful if a webhook event was missed.">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={resyncingCampaignId === campaign.campaign_id}
                                    onClick={() => resyncCampaignAnalytics(campaign)}
                                    data-testid={`resync-analytics-btn-${campaign.campaign_id}`}
                                    className="text-slate-600 hover:text-slate-900"
                                  >
                                    <RefreshCw
                                      size={14}
                                      className={resyncingCampaignId === campaign.campaign_id ? 'animate-spin mr-1.5' : 'mr-1.5'}
                                    />
                                    {resyncingCampaignId === campaign.campaign_id ? 'Syncing…' : 'Re-sync'}
                                  </Button>
                                </SimpleTooltip>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Contact Sync Tab */}
            <TabsContent value="contact-sync">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <ContactSyncSettings />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Add Subscriber Dialog */}
      <Dialog open={showAddSubscriber} onOpenChange={setShowAddSubscriber}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Subscriber</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email(s) *</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="subscriber@example.com&#10;Or add multiple emails separated by commas or new lines:&#10;email1@example.com, email2@example.com"
                value={newSubscriber.email}
                onChange={(e) => setNewSubscriber({ ...newSubscriber, email: e.target.value })}
              />
              <p className="text-xs text-slate-500">Add multiple emails by separating with commas or new lines</p>
            </div>
            <div className="space-y-2">
              <Label>Name (optional, for single subscriber)</Label>
              <Input
                placeholder="John Doe"
                value={newSubscriber.name}
                onChange={(e) => setNewSubscriber({ ...newSubscriber, name: e.target.value })}
                disabled={newSubscriber.email.includes(',') || newSubscriber.email.includes('\n')}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Phone (optional)</Label>
                <Input
                  placeholder="+1 555 123 4567"
                  value={newSubscriber.phone}
                  onChange={(e) => setNewSubscriber({ ...newSubscriber, phone: e.target.value })}
                  disabled={newSubscriber.email.includes(',') || newSubscriber.email.includes('\n')}
                />
              </div>
              <div className="space-y-2">
                <Label>Company (optional)</Label>
                <Input
                  placeholder="Company name"
                  value={newSubscriber.company}
                  onChange={(e) => setNewSubscriber({ ...newSubscriber, company: e.target.value })}
                  disabled={newSubscriber.email.includes(',') || newSubscriber.email.includes('\n')}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role / Segment (optional)</Label>
              <Input
                list="subscriber-role-suggestions"
                placeholder="e.g. parent, teacher, member, customer"
                value={newSubscriber.role}
                onChange={(e) => setNewSubscriber({ ...newSubscriber, role: e.target.value })}
                disabled={newSubscriber.email.includes(',') || newSubscriber.email.includes('\n')}
                data-testid="subscriber-role-input"
              />
              <datalist id="subscriber-role-suggestions">
                <option value="parent" /><option value="teacher" /><option value="student" /><option value="staff" /><option value="member" /><option value="customer" />
              </datalist>
              <p className="text-xs text-slate-500">Used for notification targeting in connected apps (e.g. Taptile)</p>
            </div>
            <div className="space-y-2">
              <Label>Preferred Language</Label>
              <Select
                value={newSubscriber.preferred_language || 'en'}
                onValueChange={(val) => setNewSubscriber({ ...newSubscriber, preferred_language: val })}
              >
                <SelectTrigger data-testid="add-subscriber-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">French (Français)</SelectItem>
                  <SelectItem value="de">German (Deutsch)</SelectItem>
                  <SelectItem value="es">Spanish (Español)</SelectItem>
                  <SelectItem value="it">Italian (Italiano)</SelectItem>
                  <SelectItem value="pt">Portuguese (Português)</SelectItem>
                  <SelectItem value="nl">Dutch (Nederlands)</SelectItem>
                  <SelectItem value="pl">Polish (Polski)</SelectItem>
                  <SelectItem value="ro">Romanian (Română)</SelectItem>
                  <SelectItem value="cs">Czech (Čeština)</SelectItem>
                  <SelectItem value="el">Greek (Ελληνικά)</SelectItem>
                  <SelectItem value="zh">Chinese (中文)</SelectItem>
                  <SelectItem value="ja">Japanese (日本語)</SelectItem>
                  <SelectItem value="ko">Korean (한국어)</SelectItem>
                  <SelectItem value="ar">Arabic (العربية)</SelectItem>
                  <SelectItem value="hi">Hindi (हिन्दी)</SelectItem>
                  <SelectItem value="ru">Russian (Русский)</SelectItem>
                  <SelectItem value="tr">Turkish (Türkçe)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Newsletters will be translated to this language when sent. Applies to all emails added above.</p>
            </div>
            {groups.length > 0 && (
              <div className="space-y-2">
                <Label>Groups (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {groups.map(group => (
                    <button
                      key={group.group_id}
                      type="button"
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        newSubscriber.group_ids.includes(group.group_id)
                          ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                      onClick={() => {
                        const ids = newSubscriber.group_ids.includes(group.group_id)
                          ? newSubscriber.group_ids.filter(id => id !== group.group_id)
                          : [...newSubscriber.group_ids, group.group_id];
                        setNewSubscriber({ ...newSubscriber, group_ids: ids });
                      }}
                    >
                      {group.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSubscriber(false)}>Cancel</Button>
            <Button onClick={handleAddSubscriber}>Add Subscriber</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Subscriber Dialog */}
      <Dialog open={showEditSubscriber} onOpenChange={setShowEditSubscriber}>
        <DialogContent className="max-w-lg" data-testid="edit-subscriber-dialog">
          <DialogHeader>
            <DialogTitle>Edit Subscriber</DialogTitle>
          </DialogHeader>
          {editingSubscriber && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editingSubscriber.email}
                  disabled
                  className="bg-slate-50"
                />
                <p className="text-xs text-slate-500">Email cannot be changed</p>
              </div>
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  placeholder="John Doe"
                  value={editingSubscriber.name || ''}
                  onChange={(e) => setEditingSubscriber({ ...editingSubscriber, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Role / Segment</Label>
                <Input
                  list="subscriber-role-suggestions"
                  placeholder="e.g. parent, teacher, member, customer"
                  value={editingSubscriber.role || ''}
                  onChange={(e) => setEditingSubscriber({ ...editingSubscriber, role: e.target.value })}
                  data-testid="edit-subscriber-role-input"
                />
                <datalist id="subscriber-role-suggestions">
                  <option value="parent" /><option value="teacher" /><option value="student" /><option value="staff" /><option value="member" /><option value="customer" />
                </datalist>
                <p className="text-xs text-slate-500">Used for notification targeting in connected apps (e.g. Taptile)</p>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={editingSubscriber.status} 
                  onValueChange={(val) => setEditingSubscriber({ ...editingSubscriber, status: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                    <SelectItem value="bounced">Bounced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Preferred Language</Label>
                <Select 
                  value={editingSubscriber.preferred_language || 'en'} 
                  onValueChange={(val) => setEditingSubscriber({ ...editingSubscriber, preferred_language: val })}
                >
                  <SelectTrigger data-testid="subscriber-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="fr">French (Français)</SelectItem>
                    <SelectItem value="de">German (Deutsch)</SelectItem>
                    <SelectItem value="es">Spanish (Español)</SelectItem>
                    <SelectItem value="it">Italian (Italiano)</SelectItem>
                    <SelectItem value="pt">Portuguese (Português)</SelectItem>
                    <SelectItem value="nl">Dutch (Nederlands)</SelectItem>
                    <SelectItem value="pl">Polish (Polski)</SelectItem>
                    <SelectItem value="ro">Romanian (Română)</SelectItem>
                    <SelectItem value="cs">Czech (Čeština)</SelectItem>
                    <SelectItem value="el">Greek (Ελληνικά)</SelectItem>
                    <SelectItem value="zh">Chinese (中文)</SelectItem>
                    <SelectItem value="ja">Japanese (日本語)</SelectItem>
                    <SelectItem value="ko">Korean (한국어)</SelectItem>
                    <SelectItem value="ar">Arabic (العربية)</SelectItem>
                    <SelectItem value="hi">Hindi (हिन्दी)</SelectItem>
                    <SelectItem value="ru">Russian (Русский)</SelectItem>
                    <SelectItem value="tr">Turkish (Türkçe)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Newsletters will be translated to this language when sent</p>
              </div>
              <div className="space-y-2">
                <Label>Groups</Label>
                {groups.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {groups.map(group => (
                      <button
                        key={group.group_id}
                        type="button"
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          editingSubscriber.group_ids.includes(group.group_id)
                            ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                        onClick={() => {
                          const ids = editingSubscriber.group_ids.includes(group.group_id)
                            ? editingSubscriber.group_ids.filter(id => id !== group.group_id)
                            : [...editingSubscriber.group_ids, group.group_id];
                          setEditingSubscriber({ ...editingSubscriber, group_ids: ids });
                        }}
                      >
                        {group.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No groups available. Create groups in the Groups tab.</p>
                )}
              </div>
              {editingSubscriber.created_at && (
                <div className="pt-2 border-t text-sm text-slate-500">
                  <p>Subscribed: {new Date(editingSubscriber.created_at).toLocaleString()}</p>
                  {editingSubscriber.source && <p>Source: {editingSubscriber.source}</p>}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSubscriber(false)}>Cancel</Button>
            <Button onClick={handleUpdateSubscriber}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add/Edit Group Dialog */}
      <Dialog open={showAddGroup} onOpenChange={setShowAddGroup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Group' : 'Create Group'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Group Name *</Label>
              <Input
                placeholder="e.g., VIP Customers"
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                placeholder="Brief description of this group"
                value={newGroup.description}
                onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddGroup(false)}>Cancel</Button>
            <Button onClick={handleAddGroup}>{editingGroup ? 'Update Group' : 'Create Group'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Send Newsletter Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="send-newsletter-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">Send Newsletter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {selectedNewsletter && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{selectedNewsletter.title}</p>
                <p className="text-sm text-slate-500">{selectedNewsletter.subject}</p>
              </div>
            )}
            
            {/* Recipients Summary */}
            {(sendToGroups.length > 0 || sendToSubscribers.length > 0 || sendToEmails.trim()) && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-sm font-medium text-emerald-800 flex items-center gap-2 mb-2">
                  <Users size={16} />
                  Selected: {sendToGroups.length} group(s), {sendToSubscribers.length} subscriber(s)
                  {sendToEmails.split(/[,\n]/).filter(e => e.trim() && e.includes('@')).length > 0 && 
                    `, ${sendToEmails.split(/[,\n]/).filter(e => e.trim() && e.includes('@')).length} custom email(s)`
                  }
                </p>
                <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                  {sendToGroups.map(gid => {
                    const group = groups.find(g => g.group_id === gid);
                    return group && (
                      <span key={gid} className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs flex items-center gap-1">
                        {group.name} ({group.subscriber_count || 0})
                        <button 
                          onClick={() => setSendToGroups(sendToGroups.filter(id => id !== gid))}
                          className="ml-1 hover:text-indigo-900"
                        >×</button>
                      </span>
                    );
                  })}
                  {sendToSubscribers.slice(0, 10).map(subId => {
                    const sub = subscribers.find(s => s.subscriber_id === subId);
                    return sub && (
                      <span key={subId} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs flex items-center gap-1">
                        {sub.email.length > 25 ? sub.email.substring(0, 25) + '...' : sub.email}
                        <button 
                          onClick={() => setSendToSubscribers(sendToSubscribers.filter(id => id !== subId))}
                          className="ml-1 hover:text-blue-900"
                        >×</button>
                      </span>
                    );
                  })}
                  {sendToSubscribers.length > 10 && (
                    <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs">
                      +{sendToSubscribers.length - 10} more subscribers
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Groups Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Tag size={16} className="text-indigo-600" />
                Send to Groups
              </Label>
              {groups.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {groups.map(group => (
                    <button
                      key={group.group_id}
                      type="button"
                      className={`px-3 py-1.5 rounded-lg text-sm border-2 transition-all ${
                        sendToGroups.includes(group.group_id)
                          ? 'bg-indigo-100 border-indigo-400 text-indigo-700 font-medium'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300 hover:bg-indigo-50'
                      }`}
                      onClick={() => {
                        const ids = sendToGroups.includes(group.group_id)
                          ? sendToGroups.filter(id => id !== group.group_id)
                          : [...sendToGroups, group.group_id];
                        setSendToGroups(ids);
                      }}
                    >
                      {sendToGroups.includes(group.group_id) && <CheckCircle2 size={14} className="inline mr-1" />}
                      {group.name} <span className="text-slate-400 ml-1">({group.subscriber_count || 0})</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">No groups created yet.</p>
              )}
            </div>
            
            {/* Individual Subscribers Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Users size={16} className="text-blue-600" />
                  Select Individual Subscribers
                  {sendToSubscribers.length > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-1">
                      {sendToSubscribers.length} selected
                    </span>
                  )}
                </Label>
              </div>
              
              {/* Select All Button - More Prominent */}
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Button
                  variant="default"
                  size="sm"
                  onClick={async () => {
                    // Select all active subscribers
                    try {
                      const response = await api.get('/subscribers?page=1&per_page=10000');
                      const allActive = (response.data.subscribers || [])
                        .filter(s => s.status === 'active')
                        .map(s => s.subscriber_id);
                      setSendToSubscribers(allActive);
                      toast.success(`Selected all ${allActive.length} active subscribers`);
                    } catch (error) {
                      toast.error('Failed to select all subscribers');
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="select-all-subscribers-send"
                >
                  <CheckCircle2 size={16} className="mr-2" />
                  Select All Subscribers ({subscriberTotal} total)
                </Button>
                {sendToSubscribers.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSendToSubscribers([])}
                    className="text-slate-600"
                  >
                    Clear Selection
                  </Button>
                )}
              </div>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  placeholder="Search subscribers..."
                  value={subscriberSearchSend}
                  onChange={(e) => setSubscriberSearchSend(e.target.value)}
                  className="pl-9"
                />
              </div>
              {subscribers.length > 0 ? (
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {subscribers
                    .filter(s => s.status === 'active')
                    .filter(s => 
                      !subscriberSearchSend || 
                      s.email.toLowerCase().includes(subscriberSearchSend.toLowerCase()) ||
                      (s.name || '').toLowerCase().includes(subscriberSearchSend.toLowerCase())
                    )
                    .map(sub => (
                      <div 
                        key={sub.subscriber_id}
                        className={`flex items-center justify-between px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                          sendToSubscribers.includes(sub.subscriber_id) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          const ids = sendToSubscribers.includes(sub.subscriber_id)
                            ? sendToSubscribers.filter(id => id !== sub.subscriber_id)
                            : [...sendToSubscribers, sub.subscriber_id];
                          setSendToSubscribers(ids);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            checked={sendToSubscribers.includes(sub.subscriber_id)}
                            className="pointer-events-none"
                          />
                          <div>
                            <p className="text-sm font-medium">{sub.email}</p>
                            {sub.name && <p className="text-xs text-slate-500">{sub.name}</p>}
                          </div>
                        </div>
                        {sub.group_ids?.length > 0 && (
                          <span className="text-xs text-slate-400">{sub.group_ids.length} group(s)</span>
                        )}
                      </div>
                    ))}
                  {subscribers.filter(s => s.status === 'active').length === 0 && (
                    <p className="text-sm text-slate-500 p-3 text-center">No active subscribers</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">No subscribers yet.</p>
              )}
            </div>
            
            {/* Custom Email Addresses */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Mail size={16} className="text-amber-600" />
                Additional Email Addresses
              </Label>
              <textarea
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Enter emails (comma or newline separated)"
                value={sendToEmails}
                onChange={(e) => setSendToEmails(e.target.value)}
              />
            </div>
            
            {/* Send Mode Selection */}
            <div className="space-y-3 pt-3 border-t">
              <Label className="text-sm font-medium">When to Send</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    sendMode === 'now'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSendMode('now')}
                  data-testid="send-now-option"
                >
                  <Send size={16} />
                  <span className="font-medium text-sm">Send Now</span>
                </button>
                <button
                  type="button"
                  className={`flex-1 px-3 py-2 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    sendMode === 'schedule'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSendMode('schedule')}
                  data-testid="schedule-option"
                >
                  <Calendar size={16} />
                  <span className="font-medium text-sm">Schedule</span>
                </button>
              </div>
              
              {/* Schedule Date/Time Picker */}
              {sendMode === 'schedule' && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Date</Label>
                      <Input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="schedule-date"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Time</Label>
                      <Input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        data-testid="schedule-time"
                        className="h-9"
                      />
                    </div>
                  </div>
                  {scheduleDate && scheduleTime && (
                    <p className="text-sm text-indigo-600 flex items-center gap-1">
                      <Clock size={14} />
                      Will be sent on {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSendNewsletter} 
              disabled={sending} 
              className={sendMode === 'schedule' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="send-newsletter-submit"
            >
              {sending ? 'Processing...' : sendMode === 'schedule' ? 'Schedule Newsletter' : 'Send Newsletter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Test Email Dialog */}
      <Dialog open={showTestEmail} onOpenChange={setShowTestEmail}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Send a test email to verify your SendGrid configuration is working correctly.
            </p>
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestEmail(false)}>Cancel</Button>
            <Button onClick={handleSendTestEmail} disabled={sendingTest}>
              {sendingTest ? 'Sending...' : 'Send Test Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CSV Import Dialog */}
      <Dialog open={showCsvImport} onOpenChange={setShowCsvImport}>
        <DialogContent className="max-w-2xl" data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet size={20} className="text-indigo-600" />
              Import Subscribers from CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* File Upload */}
            {!csvFile ? (
              <div className="space-y-4">
                <div 
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                  onClick={() => csvInputRef.current?.click()}
                >
                  <Upload size={40} className="mx-auto mb-4 text-slate-400" />
                  <p className="text-slate-600 mb-2">Click to upload a CSV file</p>
                  <p className="text-sm text-slate-400">Supports .csv files with email addresses</p>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleCsvFileSelect}
                    data-testid="csv-file-input"
                  />
                </div>
                
                {/* CSV Format Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                    <FileSpreadsheet size={16} />
                    CSV Format Guide
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">Your CSV can include any of these columns (only email is required):</p>
                  <div className="bg-white rounded border border-blue-200 p-3 font-mono text-xs overflow-x-auto">
                    <div className="text-slate-500">email,name,group,language</div>
                    <div className="text-slate-700">john@example.com,John Doe,Members,en</div>
                    <div className="text-slate-700">marie@example.fr,Marie Dupont,Board,fr</div>
                    <div className="text-slate-700">hans@example.de,Hans Mueller,Members,de</div>
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-blue-600">
                    <p><strong>Language codes:</strong> en (English), fr (French), de (German), es (Spanish), it (Italian), pt (Portuguese), nl (Dutch), pl (Polish), da (Danish), sv (Swedish), etc.</p>
                    <p><strong>Groups:</strong> New groups are created automatically if they don't exist.</p>
                    <p><strong>Duplicates:</strong> Existing subscribers will have their group and language updated.</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* File Info */}
                <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet size={24} className="text-emerald-600" />
                    <div>
                      <p className="font-medium">{csvFile.name}</p>
                      <p className="text-sm text-slate-500">{csvPreview.length - (csvMapping.has_header ? 1 : 0)} data rows found</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={resetCsvImport}>
                    <XCircle size={18} className="text-slate-400" />
                  </Button>
                </div>
                
                {/* Preview Table */}
                {csvPreview.length > 0 && (
                  <div>
                    <Label className="mb-2 block">Preview (first 5 rows)</Label>
                    <div className="border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            {csvPreview[0]?.map((_, colIdx) => (
                              <th key={colIdx} className="px-3 py-2 text-left text-xs font-medium text-slate-500">
                                Column {colIdx + 1}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreview.slice(0, 5).map((row, rowIdx) => (
                            <tr key={rowIdx} className={rowIdx === 0 && csvMapping.has_header ? 'bg-amber-50' : 'hover:bg-slate-50'}>
                              {row.map((cell, colIdx) => (
                                <td key={colIdx} className="px-3 py-2 border-t text-slate-600 truncate max-w-[150px]">
                                  {cell || <span className="text-slate-300">empty</span>}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {csvMapping.has_header && (
                      <p className="text-xs text-amber-600 mt-1">First row highlighted will be treated as header</p>
                    )}
                  </div>
                )}
                
                {/* Column Mapping */}
                <div className="space-y-4">
                  <Label>Column Mapping</Label>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Email Column */}
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Email Column *</Label>
                      <Select 
                        value={String(csvMapping.email_column)} 
                        onValueChange={(val) => setCsvMapping(prev => ({ ...prev, email_column: parseInt(val) }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          {csvColumns.map((colIdx) => (
                            <SelectItem key={colIdx} value={String(colIdx)}>
                              Column {colIdx + 1} {csvPreview[0]?.[colIdx] && csvMapping.has_header ? `(${csvPreview[0][colIdx]})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Name Column */}
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Name Column (optional)</Label>
                      <Select 
                        value={csvMapping.name_column !== null ? String(csvMapping.name_column) : 'none'} 
                        onValueChange={(val) => setCsvMapping(prev => ({ ...prev, name_column: val === 'none' ? null : parseInt(val) }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {csvColumns.map((colIdx) => (
                            <SelectItem key={colIdx} value={String(colIdx)}>
                              Column {colIdx + 1} {csvPreview[0]?.[colIdx] && csvMapping.has_header ? `(${csvPreview[0][colIdx]})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Group Column */}
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Group Column (optional)</Label>
                      <Select 
                        value={csvMapping.group_column !== null ? String(csvMapping.group_column) : 'none'} 
                        onValueChange={(val) => setCsvMapping(prev => ({ ...prev, group_column: val === 'none' ? null : parseInt(val) }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {csvColumns.map((colIdx) => (
                            <SelectItem key={colIdx} value={String(colIdx)}>
                              Column {colIdx + 1} {csvPreview[0]?.[colIdx] && csvMapping.has_header ? `(${csvPreview[0][colIdx]})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">Groups will be auto-created if they don't exist</p>
                    </div>
                    
                    {/* Role Column */}
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Role Column (optional)</Label>
                      <Select 
                        value={csvMapping.role_column !== null ? String(csvMapping.role_column) : 'none'} 
                        onValueChange={(val) => setCsvMapping(prev => ({ ...prev, role_column: val === 'none' ? null : parseInt(val) }))}
                      >
                        <SelectTrigger data-testid="csv-role-column-select">
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {csvColumns.map((colIdx) => (
                            <SelectItem key={colIdx} value={String(colIdx)}>
                              Column {colIdx + 1} {csvPreview[0]?.[colIdx] && csvMapping.has_header ? `(${csvPreview[0][colIdx]})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">Generic role/segment (parent, member, customer…) for app targeting</p>
                    </div>
                    
                    {/* Language Column */}
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Language Column (optional)</Label>
                      <Select 
                        value={csvMapping.language_column !== null ? String(csvMapping.language_column) : 'none'} 
                        onValueChange={(val) => setCsvMapping(prev => ({ ...prev, language_column: val === 'none' ? null : parseInt(val) }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {csvColumns.map((colIdx) => (
                            <SelectItem key={colIdx} value={String(colIdx)}>
                              Column {colIdx + 1} {csvPreview[0]?.[colIdx] && csvMapping.has_header ? `(${csvPreview[0][colIdx]})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">Use 2-letter codes: en, fr, de, es, etc.</p>
                    </div>
                    
                    {/* Has Header */}
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Options</Label>
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id="has-header"
                          checked={csvMapping.has_header}
                          onCheckedChange={(checked) => setCsvMapping(prev => ({ ...prev, has_header: !!checked }))}
                        />
                        <label htmlFor="has-header" className="text-sm text-slate-600 cursor-pointer">
                          First row is header
                        </label>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Checkbox 
                          id="override-existing"
                          checked={csvMapping.override_existing}
                          onCheckedChange={(checked) => setCsvMapping(prev => ({ ...prev, override_existing: !!checked }))}
                        />
                        <label htmlFor="override-existing" className="text-sm text-slate-600 cursor-pointer">
                          Update existing subscribers (match by email)
                        </label>
                      </div>
                      <p className="text-xs text-slate-500">When enabled, existing subscribers will be updated with new data instead of being skipped</p>
                    </div>
                  </div>
                  
                  {/* Default Groups */}
                  {groups.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Add all to groups (optional)</Label>
                      <div className="flex flex-wrap gap-2">
                        {groups.map(group => (
                          <button
                            key={group.group_id}
                            type="button"
                            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                              csvMapping.default_group_ids.includes(group.group_id)
                                ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                                : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                            }`}
                            onClick={() => {
                              const ids = csvMapping.default_group_ids.includes(group.group_id)
                                ? csvMapping.default_group_ids.filter(id => id !== group.group_id)
                                : [...csvMapping.default_group_ids, group.group_id];
                              setCsvMapping(prev => ({ ...prev, default_group_ids: ids }));
                            }}
                          >
                            {group.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCsvImport(false)}>Cancel</Button>
            <Button 
              onClick={handleCsvImport} 
              disabled={!csvFile || csvImporting}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="csv-import-submit"
            >
              {csvImporting ? 'Importing...' : 'Import Subscribers'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Group Dialog */}
      <Dialog open={showAddToGroupDialog} onOpenChange={setShowAddToGroupDialog}>
        <DialogContent className="max-w-md" data-testid="add-to-group-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus size={20} className="text-indigo-600" />
              Add to Group
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Add {selectedSubscribers.length} selected subscriber(s) to a group:
            </p>
            <Select value={addToGroupId} onValueChange={setAddToGroupId}>
              <SelectTrigger data-testid="select-group-dropdown">
                <SelectValue placeholder="Select a group..." />
              </SelectTrigger>
              <SelectContent>
                {groups.map(group => (
                  <SelectItem key={group.group_id} value={group.group_id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {groups.length === 0 && (
              <p className="text-sm text-amber-600">
                No groups available. Create a group first in the Groups tab.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToGroupDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddToGroup} 
              disabled={!addToGroupId || groups.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-add-to-group"
            >
              Add to Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Group Members Dialog */}
      <Dialog open={showGroupMembers} onOpenChange={setShowGroupMembers}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="group-members-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag size={20} className="text-indigo-600" />
              {viewingGroup?.name} - Members
            </DialogTitle>
          </DialogHeader>
          {viewingGroup && (
            <div className="py-4 space-y-4">
              {viewingGroup.description && (
                <p className="text-sm text-slate-500">{viewingGroup.description}</p>
              )}
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  placeholder="Search members..."
                  value={groupMembersSearch}
                  onChange={(e) => setGroupMembersSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              {/* Members List */}
              <div className="border rounded-lg max-h-[50vh] overflow-y-auto">
                {getGroupSubscribers(viewingGroup.group_id)
                  .filter(sub => 
                    !groupMembersSearch ||
                    sub.email.toLowerCase().includes(groupMembersSearch.toLowerCase()) ||
                    (sub.name || '').toLowerCase().includes(groupMembersSearch.toLowerCase())
                  )
                  .length === 0 ? (
                  <div className="p-8 text-center">
                    <Users size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-slate-500">No members in this group</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Email</th>
                        <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Name</th>
                        <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Other Groups</th>
                        <th className="text-right px-4 py-2 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {getGroupSubscribers(viewingGroup.group_id)
                        .filter(sub => 
                          !groupMembersSearch ||
                          sub.email.toLowerCase().includes(groupMembersSearch.toLowerCase()) ||
                          (sub.name || '').toLowerCase().includes(groupMembersSearch.toLowerCase())
                        )
                        .map(sub => {
                          const otherGroups = getGroupNames(sub.group_ids?.filter(id => id !== viewingGroup.group_id));
                          return (
                            <tr key={sub.subscriber_id} className="hover:bg-slate-50">
                              <td className="px-4 py-3 text-sm font-medium">{sub.email}</td>
                              <td className="px-4 py-3 text-sm text-slate-600">{sub.name || '-'}</td>
                              <td className="px-4 py-3">
                                {otherGroups.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {otherGroups.slice(0, 2).map((name, idx) => (
                                      <span key={idx} className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                                        {name}
                                      </span>
                                    ))}
                                    {otherGroups.length > 2 && (
                                      <span className="text-xs text-slate-400">+{otherGroups.length - 2}</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400">None</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
                                  onClick={() => handleRemoveFromGroup(sub.subscriber_id, viewingGroup.group_id)}
                                >
                                  <UserMinus size={14} className="mr-1" />
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                )}
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t text-sm text-slate-500">
                <span>{getGroupSubscribers(viewingGroup.group_id).length} total members</span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowGroupMembers(false);
                    setActiveTab('subscribers');
                  }}
                >
                  <Plus size={14} className="mr-1" />
                  Add Subscribers
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupMembers(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Newsletter Analytics Dialog */}
      <Dialog open={showNewsletterAnalytics} onOpenChange={setShowNewsletterAnalytics}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 size={20} className="text-emerald-600" />
              Campaign Analytics
            </DialogTitle>
          </DialogHeader>
          
          {analyticsLoadingDetail ? (
            <div className="py-12 text-center">
              <RefreshCw className="animate-spin mx-auto mb-2 text-slate-400" size={24} />
              <p className="text-slate-500">Loading analytics...</p>
            </div>
          ) : analyticsData ? (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Newsletter Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg">{analyticsData.title || 'Untitled Newsletter'}</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Subject: {analyticsData.subject || 'No subject'}
                </p>
                {analyticsData.sent_at && (
                  <p className="text-xs text-slate-400 mt-1">
                    Sent: {new Date(analyticsData.sent_at).toLocaleString()}
                  </p>
                )}
              </div>
              
              {/* Stats Summary */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-700">{analyticsData.stats?.total_sent || 0}</p>
                  <p className="text-xs text-blue-600">Sent</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-green-700">{analyticsData.stats?.delivered || 0}</p>
                  <p className="text-xs text-green-600">Delivered</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-emerald-700">{analyticsData.stats?.opened || 0}</p>
                  <p className="text-xs text-emerald-600">Opened</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-indigo-700">{analyticsData.stats?.clicked || 0}</p>
                  <p className="text-xs text-indigo-600">Clicked</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-red-700">{analyticsData.stats?.bounced || 0}</p>
                  <p className="text-xs text-red-600">Bounced</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-amber-700">{analyticsData.stats?.open_rate || 0}%</p>
                  <p className="text-xs text-amber-600">Open Rate</p>
                </div>
              </div>
              
              {/* Filter and Search */}
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search recipients..."
                    value={recipientSearch}
                    onChange={(e) => setRecipientSearch(e.target.value)}
                    className="pl-9"
                    data-testid="analytics-recipient-search"
                  />
                </div>
                <Select value={recipientFilter} onValueChange={setRecipientFilter}>
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="analytics-status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Recipients</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="opened">Opened</SelectItem>
                    <SelectItem value="clicked">Clicked</SelectItem>
                    <SelectItem value="bounced">Bounced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Recipients Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Email</th>
                        <th className="text-left px-4 py-2 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left px-4 py-2 text-sm font-medium text-slate-600 hidden sm:table-cell">Last Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {getFilteredRecipients().length === 0 ? (
                        <tr>
                          <td colSpan="3" className="px-4 py-8 text-center text-slate-500">
                            {analyticsData.recipients?.length === 0 
                              ? 'No recipient tracking data available for this campaign'
                              : 'No recipients match your filter'}
                          </td>
                        </tr>
                      ) : (
                        getFilteredRecipients().map((recipient, idx) => {
                          // Determine status display
                          let statusText = recipient.status || 'sent';
                          let StatusIcon = Mail;
                          let statusClass = 'bg-slate-100 text-slate-700';
                          
                          if (recipient.bounced_at || statusText.includes('bounce')) {
                            statusText = 'Bounced';
                            StatusIcon = XCircle;
                            statusClass = 'bg-red-100 text-red-700';
                          } else if (recipient.clicked_at) {
                            statusText = 'Clicked';
                            StatusIcon = CheckCircle2;
                            statusClass = 'bg-indigo-100 text-indigo-700';
                          } else if (recipient.opened_at) {
                            statusText = 'Opened';
                            StatusIcon = Eye;
                            statusClass = 'bg-emerald-100 text-emerald-700';
                          } else if (statusText === 'delivered') {
                            statusText = 'Delivered';
                            StatusIcon = MailCheck;
                            statusClass = 'bg-green-100 text-green-700';
                          } else {
                            statusText = 'Sent';
                            StatusIcon = Mail;
                            statusClass = 'bg-blue-100 text-blue-700';
                          }
                          
                          // Get latest timestamp
                          const latestTime = recipient.clicked_at || recipient.opened_at || recipient.delivered_at || recipient.sent_at;
                          
                          return (
                            <tr key={idx} className="hover:bg-slate-50" data-testid={`recipient-row-${idx}`}>
                              <td className="px-4 py-2 text-sm">
                                <span className="font-medium">{recipient.email}</span>
                                {recipient.name && (
                                  <span className="text-slate-500 ml-2">({recipient.name})</span>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}>
                                  <StatusIcon size={12} />
                                  {statusText}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-slate-500 hidden sm:table-cell">
                                {latestTime ? new Date(latestTime).toLocaleString() : '-'}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Footer Stats */}
              <div className="flex items-center justify-between text-sm text-slate-500 border-t pt-3">
                <span>
                  Showing {getFilteredRecipients().length} of {analyticsData.recipients?.length || 0} recipients
                </span>
                <span>
                  Click Rate: {analyticsData.stats?.click_rate || 0}% | Bounce Rate: {analyticsData.stats?.bounce_rate || 0}%
                </span>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <AlertTriangle size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="text-slate-500">No analytics data available</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewsletterAnalytics(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipient Details Dialog (for clickable stats) */}
      <Dialog open={showRecipientDetails} onOpenChange={setShowRecipientDetails}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {recipientDetailsType === 'opened' && <Eye className="text-emerald-600" size={20} />}
              {recipientDetailsType === 'bounced' && <AlertTriangle className="text-red-600" size={20} />}
              {recipientDetailsType === 'unsubscribed' && <UserMinus className="text-amber-600" size={20} />}
              {recipientDetailsType === 'opened' && 'Opened Emails'}
              {recipientDetailsType === 'bounced' && 'Bounced Emails'}
              {recipientDetailsType === 'unsubscribed' && 'Unsubscribed'}
              {recipientDetailsCampaign && (
                <span className="text-slate-400 font-normal text-sm ml-2">
                  - {recipientDetailsCampaign.title}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {/* Notice when per-recipient detail is incomplete vs aggregate counter */}
            {(() => {
              if (!recipientDetailsCampaign) return null;
              const aggregate = recipientDetailsType === 'bounced'
                ? (recipientDetailsCampaign.bounced_count || ((recipientDetailsCampaign.hard_bounced_count || 0) + (recipientDetailsCampaign.soft_bounced_count || 0)))
                : recipientDetailsType === 'opened'
                  ? (recipientDetailsCampaign.opened_count || 0)
                  : recipientDetailsType === 'unsubscribed'
                    ? (recipientDetailsCampaign.unsubscribed_count || 0)
                    : 0;
              if (!recipientDetailsLoading && aggregate > 0 && recipientDetailsTotal < aggregate) {
                const missing = aggregate - recipientDetailsTotal;
                return (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                    <strong>Heads up:</strong> the aggregate count for this campaign is <strong>{aggregate}</strong>, but only <strong>{recipientDetailsTotal}</strong> have per-recipient details available. {missing} record{missing === 1 ? '' : 's'} predate detailed tracking — totals are correct, but the individual addresses are unavailable for those.
                  </div>
                );
              }
              return null;
            })()}

            {/* Bounce type filter for bounced emails */}
            {recipientDetailsType === 'bounced' && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-600">Filter by bounce type:</span>
                <button
                  onClick={() => fetchRecipientDetails(recipientDetailsCampaign, 'bounced', 'all', 1)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    recipientDetailsBounceType === 'all' 
                      ? 'bg-slate-800 text-white' 
                      : 'bg-white text-slate-600 border hover:bg-slate-100'
                  }`}
                >
                  All ({recipientDetailsTotal})
                </button>
                <button
                  onClick={() => fetchRecipientDetails(recipientDetailsCampaign, 'bounced', 'hard', 1)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    recipientDetailsBounceType === 'hard' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-white text-red-600 border border-red-200 hover:bg-red-50'
                  }`}
                >
                  Hard Bounce
                </button>
                <button
                  onClick={() => fetchRecipientDetails(recipientDetailsCampaign, 'bounced', 'soft', 1)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    recipientDetailsBounceType === 'soft' 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-white text-amber-600 border border-amber-200 hover:bg-amber-50'
                  }`}
                >
                  Soft Bounce
                </button>
              </div>
            )}
            
            {recipientDetailsLoading ? (
              <div className="py-12 text-center">
                <RefreshCw className="animate-spin mx-auto mb-2 text-slate-400" size={24} />
                <p className="text-slate-500">Loading recipients...</p>
              </div>
            ) : recipientDetailsList.length === 0 ? (
              <div className="py-12 text-center">
                <Mail className="mx-auto mb-2 text-slate-300" size={32} />
                <p className="text-slate-500">No recipients found</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Email</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Name</th>
                      {recipientDetailsType === 'bounced' && (
                        <>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Type</th>
                          <th className="text-left px-4 py-3 font-medium text-slate-600">Reason</th>
                        </>
                      )}
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {recipientDetailsList.map((recipient, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium">{recipient.email}</td>
                        <td className="px-4 py-3 text-slate-600">{recipient.name || '-'}</td>
                        {recipientDetailsType === 'bounced' && (
                          <>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                recipient.bounce_type === 'hard' 
                                  ? 'bg-red-100 text-red-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {recipient.bounce_type === 'hard' ? 'Hard Bounce' : 'Soft Bounce'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px] truncate" title={recipient.bounce_reason}>
                              {recipient.bounce_reason || '-'}
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3 text-slate-500">
                          {recipientDetailsType === 'opened' && recipient.opened_at 
                            ? new Date(recipient.opened_at).toLocaleString() 
                            : recipientDetailsType === 'bounced' && recipient.bounced_at
                            ? new Date(recipient.bounced_at).toLocaleString()
                            : recipientDetailsType === 'unsubscribed' && recipient.unsubscribed_at
                            ? new Date(recipient.unsubscribed_at).toLocaleString()
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {recipientDetailsTotal > 50 && (
              <div className="flex items-center justify-between mt-4 text-sm">
                <span className="text-slate-500">
                  Showing {recipientDetailsList.length} of {recipientDetailsTotal} recipients
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={recipientDetailsPage === 1}
                    onClick={() => fetchRecipientDetails(
                      recipientDetailsCampaign, 
                      recipientDetailsType, 
                      recipientDetailsBounceType, 
                      recipientDetailsPage - 1
                    )}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-slate-600">
                    Page {recipientDetailsPage} of {Math.ceil(recipientDetailsTotal / 50)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={recipientDetailsPage >= Math.ceil(recipientDetailsTotal / 50)}
                    onClick={() => fetchRecipientDetails(
                      recipientDetailsCampaign, 
                      recipientDetailsType, 
                      recipientDetailsBounceType, 
                      recipientDetailsPage + 1
                    )}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4 mt-4">
            <Button 
              variant="outline" 
              onClick={exportRecipientDetails}
              className="mr-auto"
            >
              <FileSpreadsheet size={16} className="mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => setShowRecipientDetails(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Newsletters;
